import { formatSum } from '../../../../utils'
import { buildCloseReasonHint } from '../../../common/closeReasonHint'
import { PaymentTicketWithTransactions } from '../core/_models'
import { ticketChangeErrorText } from '../core/ticketErrors'
import { FinishedTicketRequisiteCell } from './FinishedTicketRequisiteCell'
import { IsGranted } from '../../auth/PrivateRoute'
import { ROLE, useAuth } from '../../auth'
import { convertUTCtoMSK } from '../../../../utils'
import {
  internalCancelTicketAPI,
  resendCallbackAPI,
  setFakeTicketAPI,
  ticketFileGet,
} from '../core/_requests'
import Swal from 'sweetalert2'
import withReactContent from 'sweetalert2-react-content'
import { ChangeEvent, Dispatch, MouseEvent, SetStateAction, useEffect, useState } from 'react'
import Select, { SingleValue } from 'react-select'
import InfoHint from '../../../common/InfoHint'
import { SelectStyles } from '../../../../components/Select/Select.theme'
import { useThemeMode } from '../../../../_metronic/partials'
import { Accordion } from 'radix-ui'
import { TableHead } from '../../../../components/Table/TableHead'
import { Table } from 'react-bootstrap'
import styles from './FinishedTicketItem.module.css'
import clsx from 'clsx'
import { useIntl } from 'react-intl'
import { FileDownloadButton } from '../../../../components/FileDownloadButton/FileDownloadButton'
import { KTIcon } from '../../../../_metronic/helpers'
import { AuditLogLink } from '../../Logs/routes/Audit/components/AuditLogLink'
import { ClientCell } from '../../../common/ClientCell'
import { DepositTokenLink } from '../../../common/DepositTokenLink'
import { escapeHtml } from '../../../common/escapeHtml'
import { FinishedTicketTransactionRow } from './FinishedTicketTransactionRow'
import { ChangeCreditedAmountModal } from '../../settlement'

const AppSwal = withReactContent(Swal)

const statusOptions = [
  {
    value: 'COMPLETED',
    label: 'Завершен',
  },
  {
    value: 'EXPIRED',
    label: 'Просрочен',
  },
]

const filterStatus = (status: string, currentUser: any, isAdminOrManager: boolean) => {
  if (status === 'EXPIRED') {
    // С "Просроченный" на "Завершённый" - только для admin, manager, cashier, senior_cashier, merchant_payments_operator
    if (IsGranted([ROLE.ROLE_ADMIN, ROLE.ROLE_MANAGER, ROLE.ROLE_CASHIER, ROLE.ROLE_SENIOR_CASHIER, ROLE.ROLE_MERCHANT_PAYMENTS_OPERATOR, ROLE.ROLE_MERCHANT_MANAGER], currentUser)) {
      return [
        {
          value: 'COMPLETED',
          label: 'Завершен',
        },
      ]
    } else {
      return [] // Нет доступных опций для операторов
    }
  } else if (status === 'COMPLETED') {
    // С "Завершённый" на "Просроченный" - admin, manager, merchant_payments_operator
    if (isAdminOrManager || IsGranted([ROLE.ROLE_MERCHANT_PAYMENTS_OPERATOR, ROLE.ROLE_MERCHANT_MANAGER], currentUser)) {
      return [
        {
          value: 'EXPIRED',
          label: 'Просрочен',
        },
      ]
    } else {
      return [] // Нет доступных опций для смены статуса
    }
  } else {
    return []
  }
}

const getStatusValue = (status: string) => {
  return statusOptions?.filter((el) => el.value === status)
}

const FinishedTicketItem = (props: {
  ticket: PaymentTicketWithTransactions
  setShowAlert: Dispatch<SetStateAction<boolean>>
  targetTicket?: PaymentTicketWithTransactions | null
  setStatusModal: Dispatch<SetStateAction<boolean>>
  setTargetTicket: Dispatch<SetStateAction<PaymentTicketWithTransactions | null>>
  setTickets: Dispatch<SetStateAction<PaymentTicketWithTransactions[]>>
}) => {
  const intl = useIntl()
  const ticket = props.ticket
  const { mode: theme } = useThemeMode()
  const [options, setOptions] = useState(statusOptions)
  const [selectValue, setSelectValue] = useState(getStatusValue(ticket?.ticketStatus))

  // Причина закрытия рядом со статусом: по самому статусу отмену клиентом и истёкший срок не
  // различить — оба приходят как EXPIRED. У незавершённых заявок бэкенд её не присылает.
  const closeReasonHint = buildCloseReasonHint(ticket.closeReasonTitle, ticket.clientCancelReasonTitle)
  const [isOpen, setIsOpen] = useState(false)
  const [changingAmount, setChangingAmount] = useState(false)
  const { currentUser } = useAuth()
  // «Изменить сумму зачисления»: the same roles as the backend; scoped roles act on their own merchants.
  const canChangeAmount =
    ticket.ticketStatus === 'COMPLETED' &&
    IsGranted([ROLE.ROLE_ADMIN, ROLE.ROLE_MANAGER, ROLE.ROLE_MERCHANT_PAYMENTS_OPERATOR, ROLE.ROLE_MERCHANT_MANAGER], currentUser)

  useEffect(() => {
    setSelectValue(getStatusValue(ticket.ticketStatus))
    setOptions(filterStatus(ticket?.ticketStatus, currentUser, IsGranted([ROLE.ROLE_ADMIN, ROLE.ROLE_MANAGER], currentUser)))
  }, [ticket, currentUser])

  const changeStatusHandler = async (
    status: SingleValue<{
      value: string
      label: string
    }>
  ) => {
    props.setTargetTicket(ticket)
    if (status?.value === 'COMPLETED') {
      props.setTickets((prev) => {
        return prev.map((el) => {
          if (el?.ticketToken === ticket?.ticketToken) {
            return { ...el, ticketStatus: 'COMPLETED' }
          }
          return el
        })
      })
      props.setStatusModal(true)
      setSelectValue([statusOptions[0]])
    } else if (status?.value === 'EXPIRED') {
      try {
        const response = await internalCancelTicketAPI(ticket?.ticketToken, currentUser)
        setSelectValue([statusOptions[1]])
        props.setTickets((prev: any) =>
          prev.map((el: PaymentTicketWithTransactions) => {
            if (el?.ticketToken === response?.data?.ticket?.token) {
              return { ...el, amount: response?.data?.ticket.amount, ticketStatus: 'EXPIRED' }
            }
            return el
          })
        )
      } catch (e) {
        Swal.fire({ title: 'Ошибка', text: ticketChangeErrorText(e), icon: 'error' })
      }
    }
  }

  const handleCopy = (e: MouseEvent) => {
    const td = e.target as HTMLElement
    navigator.clipboard.writeText(td?.innerText)
    props.setShowAlert(true)
    setTimeout(() => {
      props.setShowAlert(false)
    }, 1400)
  }

  const resendCallback = async () => {
    AppSwal.fire({
      title: 'Отправить КБ мерчанту?',
      text: 'Мерчанту будет повторно отправлен колбэк о текущем статусе тикета с актуальной подписью.',
      icon: 'question',
      showCancelButton: true,
      cancelButtonText: 'Отмена',
      confirmButtonText: 'Отправить',
      showLoaderOnConfirm: true,
      preConfirm: async () => {
        try {
          const response = await resendCallbackAPI(ticket.ticketToken)
          if (!response.data?.success) {
            throw new Error('сервис вернул ошибку')
          }
        } catch (err) {
          AppSwal.showValidationMessage(escapeHtml(`Не удалось отправить КБ: ${err}`))
        }
      },
      allowOutsideClick: () => !Swal.isLoading(),
    }).then((result) => {
      if (result.isConfirmed) {
        AppSwal.fire({
          title: 'КБ отправлен',
          icon: 'success',
          timer: 1500,
        })
      }
    })
  }

  const fakeHandler = async () => {
    const response = await setFakeTicketAPI(ticket.ticketToken)
    props.setTickets((prev) =>
      prev.map((el) =>
        el.ticketToken === response.data.ticket.token
          ? { ...el, isFake: response.data.ticket.isFake }
          : el
      )
    )
  }

  return (
    <>
      <Accordion.Root
        type='single'
        collapsible
        asChild
        className={styles.AccordionRoot}
        onValueChange={(value) => {
          if (ticket?.transactions && ticket?.transactions?.length > 0) {
            setIsOpen(() => Boolean(value))
          }
        }}
      >
        <Accordion.Item value='item-1' asChild className={styles.AccordionItem}>
          <tr
            className={clsx(
              ticket?.transactions && ticket?.transactions?.length > 0 && styles.pointer
            )}
            onClick={(e) => {
              if (
                e.target instanceof Element &&
                !e.target.closest('.statusSelect') &&
                !e.target.closest('.downloadLink') &&
                !e.target.closest('.fakeCheckbox')
              ) {
                setIsOpen((prev) => !prev)
              }
            }}
          >
            <td>
              <p className='mb-4'><DepositTokenLink token={ticket?.ticketToken} /></p>
              <div className='d-flex-column'>
                <p className='fw-bold fs-9'>ID транзакции:</p>
                <span className='fs-9'>{ticket?.merchantTransactionId}</span>
              </div>
            </td>
            <td>{ticket.amount ? formatSum(ticket.amount) : '-'}</td>
            <td>{ticket?.paymentMethod}</td>
            <td>{ticket.createdAt ? convertUTCtoMSK(ticket?.createdAt) : '-'}</td>
            <td>{ticket.completionDate ? convertUTCtoMSK(ticket?.completionDate) : '-'}</td>
            <ClientCell externalId={ticket?.externalUserId} token={ticket?.clientToken} />
            {IsGranted([ROLE.ROLE_ADMIN, ROLE.ROLE_MANAGER, ROLE.ROLE_MERCHANT_PAYMENTS_OPERATOR, ROLE.ROLE_MERCHANT_MANAGER], currentUser) && (
              <td>{ticket.merchant || '-'}</td>
            )}
            <td>{ticket.author || '-'}</td>
            <td>{ticket.executor || '-'}</td>
            {IsGranted([ROLE.ROLE_OPERATOR, ROLE.ROLE_SENIOR_OPERATOR], currentUser) && (
              <FinishedTicketRequisiteCell requisite={ticket.requisite} />
            )}
            <td>
              <span className='badge badge-pill badge-info'>
                {intl.formatMessage({ id: `ticket.status.${ticket.completionType}` })}
              </span>
            </td>
            <td className='downloadLink'>
              {ticket?.downloadLink ? (                
                <FileDownloadButton url={ticket?.downloadLink} badgeStatus={ticket?.autoCheckStatus} badgekMessage={ticket?.autoCheckMessage} badgePosition='end'/>
              ) : (
                '-'
              )}
            </td>
            <td>{ticket.sms || '-'}</td>
            <td className='fakeCheckbox'>
              <input
                onChange={fakeHandler}
                className='form-check-input'
                type='checkbox'
                value=''
                id='flexCheckDefault'
                checked={ticket?.isFake}
              />
            </td>
            {IsGranted(
              [
                ROLE.ROLE_ADMIN,
                ROLE.ROLE_MANAGER,
                ROLE.ROLE_CASHIER,
                ROLE.ROLE_SENIOR_CASHIER,
                ROLE.ROLE_OPERATOR,
                ROLE.ROLE_SENIOR_OPERATOR,
                ROLE.ROLE_MERCHANT_PAYMENTS_OPERATOR, ROLE.ROLE_MERCHANT_MANAGER,
              ],
              currentUser
            ) ? (
              <td className='w-150px statusSelect'>
                {ticket.ticketStatus === 'DECLINED' ? (
                  <p className='text-center mb-0'>
                    Отменен
                    <InfoHint text={closeReasonHint} />
                  </p>
                ) : (
                  // Показывать селект если:
                  // 1. Admin/Manager - всегда
                  // 2. Cashier/Senior_cashier - только если есть доступные опции
                  // 3. Operator/Senior_operator - никогда не показывать селект
                  (!IsGranted([ROLE.ROLE_OPERATOR, ROLE.ROLE_SENIOR_OPERATOR], currentUser) && 
                  (IsGranted([ROLE.ROLE_ADMIN, ROLE.ROLE_MANAGER], currentUser) || options.length > 0)) ? (
                    <div className='d-flex align-items-center'>
                      <div className='flex-grow-1'>
                        <Select
                          styles={SelectStyles(theme)}
                          onChange={changeStatusHandler}
                          options={options}
                          hideSelectedOptions={true}
                          value={selectValue}
                          menuPosition='fixed'
                          defaultValue={getStatusValue(ticket?.ticketStatus)}
                        />
                      </div>
                      <InfoHint text={closeReasonHint} />
                    </div>
                  ) : (
                    <p className='text-center mb-0'>
                      {ticket.ticketStatus === 'COMPLETED' ? 'Завершен' : 
                       ticket.ticketStatus === 'EXPIRED' ? 'Просрочен' : ticket.ticketStatus}
                      <InfoHint text={closeReasonHint} />
                    </p>
                  )
                )}
              </td>
            ) : (
              <></>
            )}
            <td className='text-end'>
              {canChangeAmount && (
                <button
                  className='btn btn-icon btn-bg-light btn-active-color-primary btn-sm me-1 fs-6'
                  onClick={(e) => {
                    e.stopPropagation()
                    setChangingAmount(true)
                  }}
                  title='Изменить сумму зачисления'
                  aria-label='Изменить сумму зачисления'
                >
                  <KTIcon className='fs-1' iconName='pencil' />
                </button>
              )}
              {/* Повторная отправка КБ: список ролей обязан совпадать с бэкендом — там IsGranted на
                  POST /api/internal/callback/resend плюс правило в security.yaml. Оператору платежей
                  мерчанта бэкенд сужает отправку своими мерчантами. */}
              {IsGranted([ROLE.ROLE_ADMIN, ROLE.ROLE_MANAGER, ROLE.ROLE_MERCHANT_PAYMENTS_OPERATOR], currentUser) && (
                <button
                  className='btn btn-icon btn-bg-light btn-active-color-primary btn-sm me-1 fs-6'
                  onClick={(e) => {
                    e.stopPropagation()
                    resendCallback()
                  }}
                  title='Повторно отправить мерчанту колбэк о текущем статусе тикета'
                >
                  <KTIcon className='fs-1' iconName='arrows-circle' />
                </button>
              )}
              <AuditLogLink ticketToken={ticket.ticketToken} />
            </td>
          </tr>
        </Accordion.Item>
      </Accordion.Root>
      {canChangeAmount && (
        <ChangeCreditedAmountModal
          show={changingAmount}
          token={ticket.ticketToken}
          currentAmount={ticket.amount ?? null}
          onClose={() => setChangingAmount(false)}
          onChanged={(amount) =>
            props.setTickets((prev) => prev.map((el) => (el.ticketToken === ticket.ticketToken ? { ...el, amount } : el)))
          }
        />
      )}
      {isOpen ? (
        <>
          {ticket?.transactions && ticket?.transactions?.length > 0 && (
            <tr>
              <td colSpan={17}>
                <Table bordered striped={false}>
                  <TableHead>
                    <th className='min-w-25px'></th>
                    <th className='min-w-150px max-w-150px ps-3'>ID адаптера</th>
                    <th className='min-w-140px'>Дата создания</th>
                    <th className='min-w-140px'>Дата закрытия</th>
                    <th className='min-w-100px'>Статус</th>
                    <th className='min-w-120px'>Адптер</th>
                    {IsGranted(
                      [
                        ROLE.ROLE_ADMIN,
                        ROLE.ROLE_MANAGER,
                        ROLE.ROLE_CASHIER,
                        ROLE.ROLE_SENIOR_CASHIER,
                      ],
                      currentUser
                    ) && <th className='min-w-150px'>Название реквизита</th>}

                    <th>Причина отмены</th>
                    <th className='min-w-25px'></th>
                    <th className='min-w-50px'></th>
                  </TableHead>
                  {props?.ticket?.transactions?.map((transaction) => (
                    <FinishedTicketTransactionRow
                      key={transaction.paymentId}
                      transaction={transaction}
                      showRequisiteName={IsGranted(
                        [
                          ROLE.ROLE_ADMIN,
                          ROLE.ROLE_MANAGER,
                          ROLE.ROLE_CASHIER,
                          ROLE.ROLE_SENIOR_CASHIER,
                        ],
                        currentUser
                      )}
                      onCopy={handleCopy}
                    />
                  ))}
                </Table>
              </td>
            </tr>
          )}
        </>
      ) : (
        <></>
      )}
    </>
  )
}

export default FinishedTicketItem
